// frontend/src/App.js

import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { useEffect } from "react";

import Navbar from "./components/Navbar";
import SeoManager from "./components/SeoManager";
import { Toaster } from "./utils/toast";
import PrivateRoute from "./components/PrivateRoute";
import { AuthProvider, useAuth } from "./hooks/useAuth";

// Auth pages
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";

// Public / student pages
import About from "./pages/About";
import Contact from "./pages/Contact";
import Consultancies from "./pages/Consultancies";
import Destinations from "./pages/Destinations";
import BlogMarketplace from "./pages/BlogMarketplace";
import BlogCompareTrust from "./pages/BlogCompareTrust";
import ConsultancyDetails from "./pages/ConsultancyDetails";
import Wishlist from "./pages/Wishlist";
import StudentDashboard from "./pages/StudentDashboard";
import StudentProfile from "./pages/StudentProfile";
import StudentPreferences from "./pages/StudentPreferences";
import StudentActivity from "./pages/StudentActivity";

// Owner pages
import OwnerLayout from "./components/owner/OwnerLayout";
import OwnerDashboard from "./pages/OwnerDashboard";
import OwnerAddOffer from "./pages/OwnerAddOffer";
import OwnerLeads from "./pages/OwnerLeads";
import OwnerSettings from "./pages/OwnerSettings";
import OwnerEditOffer from "./pages/OwnerEditOffer";


// Admin pages
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminConsultancies from "./pages/admin/AdminConsultancies";
import AdminStudents from "./pages/admin/AdminStudents";
import AdminOffers from "./pages/admin/AdminOffers";
import { BACKEND_URL } from "./config";

/* --------------------------
        APP SHELL
---------------------------*/

function AppShell() {
  const location = useLocation();
  const hideNavbar =
    location.pathname.startsWith("/owner") ||
    location.pathname.startsWith("/admin");

  const { isAuthenticated } = useAuth();

  useEffect(() => {
    const key = `aj_visit_beacon_${new Date().toISOString().slice(0, 10)}`;
    if (window.localStorage.getItem(key)) return;

    // Fire a once-per-day anonymous ping so the backend can track active visits.
    fetch(`${BACKEND_URL}/analytics/visit`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ source: "frontend_beacon" }),
      keepalive: true,
    })
      .then(() => {
        window.localStorage.setItem(key, "1");
      })
      .catch(() => {
        // silent fail: user experience should never break for analytics
      });
  }, []);

  return (
    <>
      <SeoManager />
      {!hideNavbar && <Navbar />}

      <Routes>
        {/* PUBLIC ROUTES */}
        <Route path="/" element={<About />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/destinations" element={<Destinations />} />
        <Route path="/offers" element={<Navigate to="/consultancies?offersOnly=true" replace />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />

        {/* CONSULTANCIES LIST — PUBLIC */}
        <Route path="/consultancies" element={<Consultancies />} />
        <Route
          path="/blog/study-abroad-consultancy-marketplace"
          element={<BlogMarketplace />}
        />
        <Route
          path="/blog/comparing-and-trust-abroadjacks"
          element={<BlogCompareTrust />}
        />

        {/* CONSULTANCY DETAILS — Any logged-in user */}
        <Route
          path="/consultancy/:id"
          element={
            isAuthenticated ? (
              <ConsultancyDetails />
            ) : (
              <Navigate
                to="/login"
                state={{ from: location.pathname }}
                replace
              />
            )
          }
        />

        {/* WISHLIST */}
        <Route
          path="/wishlist"
          element={
            <PrivateRoute requiredRole="student">
              <Wishlist />
            </PrivateRoute>
          }
        />

        {/* STUDENT PAGES */}
        <Route
          path="/student/dashboard"
          element={
            <PrivateRoute requiredRole="student">
              <StudentDashboard />
            </PrivateRoute>
          }
        />

        <Route
          path="/student/profile"
          element={
            <PrivateRoute requiredRole="student">
              <StudentProfile />
            </PrivateRoute>
          }
        />

        <Route
          path="/student/preferences"
          element={
            <PrivateRoute requiredRole="student">
              <StudentPreferences />
            </PrivateRoute>
          }
        />

        <Route
          path="/student/activity"
          element={
            <PrivateRoute requiredRole="student">
              <StudentActivity />
            </PrivateRoute>
          }
        />

        {/* ADMIN ROUTES */}
        <Route
          path="/admin"
          element={
            <PrivateRoute requiredRole="admin">
              <AdminLayout />
            </PrivateRoute>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route path="consultancies" element={<AdminConsultancies />} />
          <Route path="students" element={<AdminStudents />} />
          <Route path="offers" element={<AdminOffers />} />
        </Route>

        {/* OWNER ROUTES */}
        <Route
          path="/owner"
          element={
            <PrivateRoute requiredRole="owner">
              <OwnerLayout />
            </PrivateRoute>
          }
        >
          <Route index element={<OwnerDashboard />} />
          <Route path="dashboard" element={<OwnerDashboard />} />
          <Route path="add-offer" element={<OwnerAddOffer />} />
          <Route path="leads" element={<OwnerLeads />} />
          <Route path="settings" element={<OwnerSettings />} />
          <Route path="edit-offer/:offerId" element={<OwnerEditOffer />} />

        </Route>
      </Routes>
    </>
  );
}

/* --------------------------
          MAIN APP
---------------------------*/

function App() {
  return (
    <Router>
      <AuthProvider>
        <Toaster position="top-right" />
        <AppShell />
      </AuthProvider>
    </Router>
  );
}

export default App;
